<template>
  <v-autocomplete
    v-model="selectedItems"
    :search-input.sync="search"
    :items="items"
    chips
    small-chips
    deletable-chips
    hide-details
    label="Search assets"
    multiple
    clearable
    return-object
    item-text="title"
    item-value="id"
    class="assets-autocomplete"
    @change="handleChange"
  >
    <template v-slot:item="{ item, attrs, on }">
      <v-list-item
        :disabled="disabled"
        v-on="on"
        v-bind="attrs"
        #default="{ active }"
      >
        <v-list-item-action class="mr-5">
          <v-checkbox
            :ripple="false"
            :input-value="active"
            :disabled="disabled"
          ></v-checkbox>
        </v-list-item-action>
        <v-list-item-content>
          <v-list-item-title>
            <v-row no-gutters align="center">
              <AssetIcon
                :icon-size="14"
                :container-size="25"
                :icon="item.assetType.icon"
                :color="item.assetType.color"
              />
              <span>{{ item.title }}</span>
            </v-row>
          </v-list-item-title>
        </v-list-item-content>
      </v-list-item>
    </template>
    <template v-slot:append-item>
      <v-divider class="my-2"></v-divider>
      <v-list-item v-if="!loading" :disabled="disabled">
        <v-row>
          <v-col class="py-0 my-0 more-btn-container">
            <v-btn
              text
              color="primary"
              @click="loadMoreItems"
              :disabled="allItemsLoaded"
              >Get more assets</v-btn
            >
          </v-col>
          <v-col class="py-0 mt-2 mr-5 totals-text-container">
            {{ currentCount }} out of {{ total }}
          </v-col>
        </v-row>
      </v-list-item>
      <v-list-item v-if="loading" class="loading-more">
        <v-progress-circular indeterminate></v-progress-circular>
      </v-list-item>
    </template>
  </v-autocomplete>
</template>

<script>
import lodashDebounce from "lodash/debounce";
import lodashCloneDeep from "lodash/cloneDeep";
import lodashIsEqual from "lodash/isEqual";

import { getAssets } from "@/services/assetManagement/assets.service";

const EMITTED_ACTIONS = {
  CHANGE: "change",
};

export default {
  name: "SearchFilterAssets",
  components: {
    AssetIcon: () => import("@/components/assetManagement/AssetIcon"),
  },
  props: {
    incoming: {
      type: Array,
      default: () => [],
    },
    maxAllowed: {
      type: Number,
      required: true,
    },
  },
  data() {
    return {
      selectedItems: null,
      items: [],
      search: "",
      loading: false,
      currentPage: 0,
      pageSize: 50,
      assets: null,
    };
  },
  async created() {
    await this.fetchItems();
  },
  computed: {
    total() {
      return this.assets?.metadata?.total ? this.assets.metadata.total : 0;
    },
    currentCount() {
      return this.items.length;
    },
    allItemsLoaded() {
      return this.currentCount === this.total || this.disabled;
    },
    disabled() {
      return this.selectedItems?.length === this.maxAllowed;
    },
  },
  methods: {
    fetchItems: lodashDebounce(async function (search = "", reset = false) {
      if (reset) {
        this.currentPage = 0;
        this.items = [];
      }

      if (!this.loading) {
        this.loading = true;
        try {
          const options = {
            filterTitle: search,
            titleSortDirection: "ASC",
            fields: ["id", "title", "assetType", "latitude", "longitude"],
            itemsPerPage: this.pageSize,
            page: this.currentPage + 1,
          };
          const newItems = await getAssets({ options });
          this.assets = lodashCloneDeep(newItems);
          if (newItems?.assets?.length) {
            this.items = [...this.items, ...newItems.assets];
            this.currentPage = parseInt(newItems.metadata?.pageNumber, 10);
          }
        } catch (error) {
          console.error("Error fetching items in fetchItems:", error);
        } finally {
          this.loading = false;
        }
      }
    }, 500),
    async loadMoreItems() {
      if (this.loading) return;
      await this.fetchItems(this.search);
    },
    handleChange() {
      this.$emit(EMITTED_ACTIONS.CHANGE, this.selectedItems);
    },
    async getIncomingAssetDetails(ids) {
      if (ids && ids.length) {
        try {
          const options = {
            filterAssetIds: ids.join(),
            titleSortDirection: "ASC",
            fields: ["id", "title", "assetType", "latitude", "longitude"],
            itemsPerPage: this.maxAllowed,
          };
          const newItems = await getAssets({ options });
          const assets = lodashCloneDeep(newItems);
          if (assets?.assets?.length) {
            this.selectedItems = assets.assets;
            this.items = this.selectedItems.concat(this.items);
          }
        } catch (error) {
          console.error(
            "Error fetching items in getIncomingAssetDetails:",
            error
          );
        }
      }
    },
  },
  watch: {
    incoming: {
      async handler(newVal, oldVal) {
        if (!lodashIsEqual(newVal, oldVal)) {
          await this.getIncomingAssetDetails(newVal);
        }
      },
      immediate: true,
      deep: true,
    },
    search: {
      async handler() {
        this.currentPage = 0;
        await this.fetchItems(this.search);
      },
    },
  },
};
</script>

<style>
.assets-autocomplete .v-chip {
  height: auto;
  white-space: normal;
}
</style>

<style scoped>
.more-btn-container {
  display: flex;
  justify-content: left;
}
.totals-text-container {
  font-size: 0.75em;
  display: flex;
  justify-content: right;
}
</style>
